import { invoke } from '@tauri-apps/api/core';
import { apiClient } from '../utils/apiClient';
import { useAuthStore } from '../store/authStore';

/**
 * The account's choice about the encrypted cloud copy of its vault
 * (Standalone vault cards R2.7 / 6.3'; owner decision 08.10.2026: explicit
 * consent for EVERY account, including paid accounts that already sync).
 *
 *   GET    /me/vault/consent  → { consented_at, declined_at, included, items, max }
 *   PUT    /me/vault/consent  → "Store encrypted in my Apiboo cloud"
 *   DELETE /me/vault/consent  → "Keep on this device only" (binds every plan)
 *   DELETE /me/vault/cloud    → "Delete my cloud copy" (needs a recent sign-in)
 *
 * The answer the UI acts on is one word, `choiceOf(consent)`:
 *   'cloud' | 'device' | 'ask'
 */

/** Window event: the consent dialog should ask (fired by `ensureVaultSync`). */
export const CONSENT_NEEDED_EVENT = 'vault-cloud-consent-needed';
/** Window event: the choice changed (the settings section re-reads). */
export const CONSENT_CHANGED_EVENT = 'vault-cloud-consent-changed';

export function choiceOf(consent) {
  if (consent?.consented_at) return 'cloud';
  if (consent?.declined_at) return 'device';
  return 'ask';
}

export async function readConsent() {
  return (await apiClient.get('/me/vault/consent')).data;
}

export async function storeInCloud() {
  const data = (await apiClient.put('/me/vault/consent')).data;
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  return data;
}

/** Stops this device's vault sync; the vault itself stays exactly as it is. */
async function stopSyncHere({ forget }) {
  const status = await invoke('vault_sync_status').catch(() => null);
  if (status?.enrolled && status.scopeId) {
    await invoke('sync_unregister_workspace', { workspaceId: status.scopeId }).catch(() => {});
  }
  if (forget) {
    await invoke('vault_sync_forget_cloud');
  } else if (status?.enrolled) {
    await invoke('vault_sync_disenroll');
  }
}

export async function keepOnDevice() {
  const data = (await apiClient.delete('/me/vault/consent')).data;
  await stopSyncHere({ forget: false });
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  return data;
}

const errorOf = (e) => e?.response?.data?.error || '';

/**
 * Removes every row of this account's cloud vault on the server. A sign-in
 * older than 10 minutes is refused by the server; then the browser sign-in
 * opens once and the delete is retried (a different account aborts).
 */
export async function deleteCloudCopy({ reauth = useAuthStore.getState().loginWithBrowser } = {}) {
  const attempt = async () => (await apiClient.delete('/me/vault/cloud')).data;
  let data;
  try {
    data = await attempt();
  } catch (e) {
    if (errorOf(e) !== 'recent_auth_required') throw e;
    const before = useAuthStore.getState().user?.id;
    const result = await reauth();
    if (!result?.success) throw new Error('reauth_cancelled');
    if (before && useAuthStore.getState().user?.id !== before) throw new Error('account_changed');
    try {
      data = await attempt();
    } catch (again) {
      // The account has a second factor and that sign-in did not use it: the
      // server resets the key epoch on a delete, so it asks for the same
      // fresh second factor a passphrase change does.
      if (errorOf(again) === 'recent_auth_required' && again?.response?.data?.second_factor_required) {
        throw new Error('second_factor_required');
      }
      throw again;
    }
  }
  await stopSyncHere({ forget: true });
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  return data;
}
